/**
 * CartContext — global shopping cart state persisted in localStorage.
 *
 * Items are stored as { key, kind, ref_id, name, unit_amount_cents, quantity,
 * meta } where `key` is a stable de-dupe id (`${kind}:${ref_id}`) and `meta`
 * carries small display strings (event date, image url, etc.) — we never
 * trust the price in `unit_amount_cents` on the server; it's just for the UI.
 */
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { toast } from 'sonner';

const STORAGE_KEY = 'fda_cart_v1';

export const CartContext = createContext(null);

const readCart = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_) { return []; }
};

const writeCart = (items) => {
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch (_) { /* ignore */ }
};

export const CartProvider = ({ children }) => {
  const { API, user } = useContext(AuthContext);
  const [items, setItems] = useState(readCart);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => { writeCart(items); }, [items]);

  const addItem = useCallback((item) => {
    const key = item.key || `${item.kind}:${item.ref_id}`;
    setItems((prev) => {
      const found = prev.find((i) => i.key === key);
      if (found) {
        return prev.map((i) => i.key === key ? { ...i, quantity: i.quantity + (item.quantity || 1) } : i);
      }
      return [...prev, { ...item, key, quantity: item.quantity || 1 }];
    });
    toast.success(`Added ${item.name || 'item'} to cart`);
    setDrawerOpen(true);
  }, []);

  const updateQuantity = useCallback((key, qty) => {
    setItems((prev) => prev
      .map((i) => i.key === key ? { ...i, quantity: Math.max(1, qty) } : i)
    );
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const totalCents = useMemo(
    () => items.reduce((sum, i) => sum + (i.unit_amount_cents || 0) * i.quantity, 0),
    [items],
  );
  const count = useMemo(
    () => items.reduce((s, i) => s + i.quantity, 0),
    [items],
  );

  const checkout = useCallback(async ({ email } = {}) => {
    if (items.length === 0) { toast.error('Your cart is empty'); return; }
    setCheckingOut(true);
    try {
      const payload = {
        items: items.map((i) => ({
          kind: i.kind,
          ref_id: i.ref_id,
          quantity: i.quantity,
          bundle_indexes: i.bundle_indexes || null,
        })),
        origin_url: window.location.origin,
        email: user ? undefined : email,
      };
      const headers = {};
      const tk = (typeof window !== 'undefined') ? window.localStorage.getItem('token') : null;
      if (tk) headers.Authorization = `Bearer ${tk}`;
      const res = await axios.post(`${API}/checkout/session`, payload, { headers });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('Could not start checkout');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
    } finally {
      setCheckingOut(false);
    }
  }, [items, API, user]);

  const value = useMemo(() => ({
    items, count, totalCents,
    addItem, updateQuantity, removeItem, clear,
    drawerOpen, setDrawerOpen,
    checkout, checkingOut,
  }), [items, count, totalCents, addItem, updateQuantity, removeItem, clear, drawerOpen, checkout, checkingOut]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
  return ctx;
};
